import { debounce } from 'lodash';
import React from 'react';
import { Keyboard, StyleSheet, View } from 'react-native';

import Divider from '@components/Divider';
import Image from '@components/Image';
import { PressableOpacity } from '@components/Pressable';
import ScrollView from '@components/ScrollView';
import {
  TextInputIcon,
  TextInputRef,
  TextInputWithError,
  TextInputWithErrorProps,
} from '@components/TextInput';
import { Paragraph } from '@components/Typography';
import { Row } from '@components/View';
import { PlaceAutocompleteResult } from '@db/Places';
import {
  usePlaceAutocompleteQuery,
  usePlaceAutocompleteSelectionMutation,
  usePlaceAutocompleteSession,
} from '@db/Places/Queries';
import { errorAlert } from '@helpers/Alert';
import { hasLoggedError, logError } from '@helpers/Loggers';
import { Place } from '@types';

type PlaceAutocompleteProps = {
  onSelect: (place: Place) => void;
  initialPlace?: Pick<Place, 'displayName'>;
  types?: string;
  showPoweredBy?: boolean;
} & TextInputWithErrorProps;

const DEBOUNCE_MS = 100;
const GOOGLE_LOGO = require('../assets/images/google-logo.png');
const GOOGLE_LOGO_SIZE = 20;

export const PlaceAutocomplete = React.forwardRef<TextInputRef, PlaceAutocompleteProps>(
  function PlaceAutocomplete(
    { initialPlace, onSelect, types, showPoweredBy = true, right, testID, ...props },
    ref
  ) {
    const [text, setText] = React.useState(initialPlace?.displayName || '');
    const [selected, setSelected] = React.useState(Boolean(initialPlace));
    const [query, setQuery] = React.useState('');
    const { resetSessionToken, sessionToken } = usePlaceAutocompleteSession();
    const autocompleteQuery = usePlaceAutocompleteQuery({ query, sessionToken, types });
    const selectionMutation = usePlaceAutocompleteSelectionMutation();
    const loading = autocompleteQuery.loading || selectionMutation.loading;
    const results = autocompleteQuery.results;
    const visibleResults = query === text ? results : [];
    const showResultsPanel = visibleResults.length > 0;
    const debouncedSetQuery = React.useMemo(
      () => debounce((query: string) => setQuery(query), DEBOUNCE_MS),
      []
    );
    const initialPlaceDisplayName = initialPlace?.displayName ?? '';

    React.useEffect(() => {
      setText(initialPlaceDisplayName);
      setSelected(Boolean(initialPlaceDisplayName));
      debouncedSetQuery.cancel();
      setQuery('');
    }, [debouncedSetQuery, initialPlaceDisplayName]);

    React.useEffect(() => {
      return () => debouncedSetQuery.cancel();
    }, [debouncedSetQuery]);

    const onChangeText = (text: string) => {
      setText(text);
      setSelected(false);
      if (!text) {
        debouncedSetQuery.cancel();
        setQuery('');
      } else {
        debouncedSetQuery(text);
      }
    };

    const onResultSelected = async (result: PlaceAutocompleteResult) => {
      try {
        Keyboard.dismiss();
        setText(result.displayName);
        debouncedSetQuery.cancel();
        setQuery('');
        const selection = await selectionMutation.resolveResult(result, sessionToken);
        if (!selection) return;

        onSelect(selection.place);
        setSelected(true);
        resetSessionToken();
      } catch (error) {
        errorAlert(error, { onAction: () => onResultSelected(result) });
        setText('');
        setSelected(false);
        if (!hasLoggedError(error)) {
          logError(error);
        }
      }
    };

    const renderResult = (result: PlaceAutocompleteResult, index: number) => {
      return (
        <PressableOpacity
          onPress={() => onResultSelected(result)}
          testID={testID ? `${testID}.Result.${index}` : undefined}>
          {/* Note: need keyboard tap persistence set to `always` to enable one-touch selection */}
          <ScrollView horizontal keyboardShouldPersistTaps="always">
            <Paragraph style={styles.resultText}>{result.displayName}</Paragraph>
          </ScrollView>
        </PressableOpacity>
      );
    };

    const PoweredByGoogle = (
      <View>
        <Divider />
        <Row style={styles.powereByContainer}>
          <Paragraph>Powered by Google</Paragraph>
          <Image source={GOOGLE_LOGO} style={styles.googleLogo} />
        </Row>
      </View>
    );

    const renderResultsPanel = () => {
      if (!showResultsPanel) return null;

      return (
        <View testID={testID ? `${testID}.ResultsPanel` : undefined}>
          {visibleResults.map((result, index) => (
            <React.Fragment key={result.placeId}>
              {index > 0 ? <Divider /> : null}
              {renderResult(result, index)}
            </React.Fragment>
          ))}
          {visibleResults.length > 0 && showPoweredBy ? PoweredByGoogle : null}
        </View>
      );
    };

    return (
      <View style={styles.container}>
        <TextInputWithError
          ref={ref}
          value={text}
          onChangeText={onChangeText}
          left={<TextInputIcon icon="magnify" loading={loading} />}
          clearButtonMode="while-editing"
          autoCorrect={false}
          autoComplete="off"
          right={
            selected ? (
              <TextInputIcon icon="check" testID={testID ? `${testID}.Icon.Selected` : undefined} />
            ) : (
              right
            )
          }
          testID={testID}
          {...props}
        />
        {renderResultsPanel()}
      </View>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  googleLogo: {
    height: GOOGLE_LOGO_SIZE,
    marginLeft: 5,
    width: GOOGLE_LOGO_SIZE,
  },
  powereByContainer: {
    justifyContent: 'flex-end',
    padding: 10,
  },
  resultText: {
    padding: 10,
  },
});
